import * as React from "react"

import * as Popover from "@radix-ui/react-popover"

import { cn } from "../../cn"
import { useListboxNavigation } from "../../hooks/useListboxNavigation"
import { useScrollLockExemption } from "../../hooks/useScrollLockExemption"
import { ChevronDownIcon } from "../ChevronDownIcon/ChevronDownIcon"
import { Input } from "../Input/Input"
import { Loader } from "../Loader/Loader"
import {
  resultCountMessage,
  SearchResultsStatus,
} from "../Select/SearchResultsStatus"
import { selectDropdownContentVariants } from "../Select/Select"

export interface ComboboxOption {
  /** The text a choice puts in the field, and the option's identity. */
  value: string
  label: string
  /** Plain text: it renders inside the option's button, which holds no controls. */
  description?: string
  disabled?: boolean
}

type AccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: string; "aria-labelledby": string }

export type ComboboxProps = AccessibleName & {
  /** The field's text. It is free text: any value can be typed and kept. */
  inputValue: string
  onInputChange: (value: string) => void
  options: ComboboxOption[]
  /**
   * Called when an option is chosen. Without it, choosing an option puts its
   * value in the field; with it, the field keeps its text and the caller
   * decides what a choice does.
   */
  onSelect?: (value: string) => void
  /** Options marked with a check and `aria-selected`. */
  selectedValues?: string[]
  /**
   * Keeps the list open after a choice, for picking several in a row. With
   * `selectedValues` set, the listbox is announced as multi-select.
   */
  closeOnSelect?: boolean
  /**
   * Narrows the options to those containing the typed text. Turn it off when
   * the options already arrive filtered, such as from a server search.
   */
  filterOptions?: boolean
  /** Shown in the list while it has no options to offer. */
  emptyMessage?: React.ReactNode
  /** Shows a spinner in the field and a loading row in an empty list. */
  loading?: boolean
  placeholder?: string
  disabled?: boolean
  /**
   * Marks the field required for assistive technology. It sets no native
   * constraint, since what is required is often a choice rather than the
   * field's text.
   */
  required?: boolean
  id?: string
  "aria-describedby"?: string
  "aria-invalid"?: boolean
  className?: string
  "data-testid"?: string
}

function matches(option: ComboboxOption, query: string) {
  return (
    option.label.toLowerCase().includes(query) ||
    option.value.toLowerCase().includes(query)
  )
}

/**
 * A text field with a list of suggestions, following the WAI-ARIA combobox
 * pattern with list autocomplete and manual selection. Focus stays in the
 * field: the arrow keys move the active option through
 * `aria-activedescendant`, Enter chooses it, and Escape closes the list. Enter
 * with no active option keeps the typed text, so values outside the list can
 * be entered.
 *
 * The list opens on focus showing every option, and filters once the user
 * types. A polite live region announces how many options the text leaves.
 */
function Combobox({
  inputValue,
  onInputChange,
  options,
  onSelect,
  selectedValues,
  closeOnSelect = true,
  filterOptions = true,
  emptyMessage,
  loading = false,
  placeholder,
  disabled,
  required,
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  className,
  "data-testid": dataTestId,
}: ComboboxProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const fieldRef = React.useRef<HTMLDivElement>(null)
  const scrollableListRef = useScrollLockExemption<HTMLDivElement>()
  const listboxId = React.useId()
  const optionIdPrefix = React.useId()
  const [open, setOpen] = React.useState(false)
  // Opening shows every option; typing is what starts narrowing them.
  const [hasTyped, setHasTyped] = React.useState(false)
  // Where the active option starts when the list opens: nowhere when it opens
  // on focus, the first or last option when an arrow key opens it.
  const [openIndex, setOpenIndex] = React.useState(-1)

  const filteredOptions = React.useMemo(() => {
    const query = inputValue.trim().toLowerCase()
    if (!filterOptions || !hasTyped || query === "") {
      return options
    }
    return options.filter((option) => matches(option, query))
  }, [filterOptions, hasTyped, inputValue, options])

  const openList = React.useCallback(() => {
    if (!disabled) {
      setOpen(true)
    }
  }, [disabled])

  const closeList = React.useCallback(() => {
    setOpen(false)
    setHasTyped(false)
    setOpenIndex(-1)
  }, [])

  const choose = (option: ComboboxOption) => {
    if (onSelect) {
      onSelect(option.value)
    } else {
      onInputChange(option.value)
    }
    if (closeOnSelect) {
      closeList()
    }
    inputRef.current?.focus()
  }

  const { activeIndex, setActiveIndex, onKeyDown } = useListboxNavigation({
    items: filteredOptions,
    open,
    // No option is active until an arrow key picks one, so Enter keeps the
    // typed text.
    initialIndex: openIndex,
    onSelect: (index) => choose(filteredOptions[index]),
    hasTextInput: true,
  })
  const optionId = (index: number) => `${optionIdPrefix}-option-${index}`
  const isMultiselectable = selectedValues !== undefined && !closeOnSelect

  const showLoadingRow = loading && filteredOptions.length === 0
  const showEmptyRow =
    !loading && filteredOptions.length === 0 && emptyMessage !== undefined
  const isListOpen =
    open && (filteredOptions.length > 0 || showLoadingRow || showEmptyRow)
  const activeOptionId =
    isListOpen && activeIndex >= 0 && activeIndex < filteredOptions.length
      ? optionId(activeIndex)
      : undefined

  React.useEffect(() => {
    if (activeOptionId) {
      document
        .getElementById(activeOptionId)
        ?.scrollIntoView?.({ block: "nearest" })
    }
  }, [activeOptionId])

  const announcement =
    open && hasTyped && !loading
      ? resultCountMessage(inputValue, filteredOptions.length)
      : ""

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        if (!open || activeIndex < 0) {
          // Opening the list or starting from no active option, ArrowDown
          // goes to the first option and ArrowUp to the last.
          event.preventDefault()
          const edge =
            event.key === "ArrowDown"
              ? filteredOptions.findIndex((option) => !option.disabled)
              : filteredOptions.findLastIndex((option) => !option.disabled)
          if (!open) {
            setOpenIndex(edge)
            openList()
          } else if (edge >= 0) {
            setActiveIndex(edge)
          }
          return
        }
        onKeyDown(event)
        return
      case "Enter":
        if (isListOpen && activeIndex >= 0) {
          onKeyDown(event)
          return
        }
        if (open) {
          closeList()
        }
        return
      case "Escape":
        if (isListOpen) {
          // The list closes and nothing behind it, such as a dialog, does.
          event.preventDefault()
          event.stopPropagation()
        }
        // With no list showing, the key belongs to whatever is behind it.
        closeList()
        return
      case "Tab":
        closeList()
    }
  }

  return (
    <Popover.Root
      open={isListOpen}
      onOpenChange={(next) => (next ? openList() : closeList())}
    >
      <Popover.Anchor asChild>
        <div ref={fieldRef} className={cn("w-full", className)}>
          <Input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid || undefined}
            aria-required={required || undefined}
            aria-autocomplete="list"
            aria-expanded={isListOpen}
            aria-busy={loading || undefined}
            // Points at the listbox only while it is rendered; a closed list
            // has no element for the reference to resolve to.
            aria-controls={isListOpen ? listboxId : undefined}
            aria-activedescendant={activeOptionId}
            autoComplete="off"
            placeholder={placeholder}
            disabled={disabled}
            value={inputValue}
            data-testid={dataTestId}
            // Always set, so the input keeps its element (and focus) when
            // loading starts or stops.
            endAdornment={
              loading ? (
                <Loader size="sm" label="Loading" />
              ) : (
                <ChevronDownIcon />
              )
            }
            onFocus={openList}
            // Options and the list keep focus in the field, so a blur means
            // focus has left the combobox and the list must not open later,
            // such as when slow options arrive.
            onBlur={closeList}
            onClick={openList}
            onChange={(event) => {
              onInputChange(event.target.value)
              setHasTyped(true)
              setOpenIndex(-1)
              setActiveIndex(-1)
              openList()
            }}
            onKeyDown={handleKeyDown}
          />
          {/* Beside the field rather than in the list, so a search that
                empties and closes the list is still announced. */}
          <SearchResultsStatus announcement={announcement} />
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          className={cn(
            selectDropdownContentVariants(),
            "w-[var(--radix-popover-trigger-width)]",
          )}
          sideOffset={4}
          align="start"
          // Focus stays in the field while the list is open.
          onOpenAutoFocus={(event) => event.preventDefault()}
          // Clicking an option or the list's scrollbar keeps focus in the
          // field.
          onMouseDown={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (
              event.target instanceof Node &&
              fieldRef.current?.contains(event.target)
            ) {
              event.preventDefault()
            }
          }}
        >
          <div
            ref={scrollableListRef}
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-busy={loading || undefined}
            aria-multiselectable={isMultiselectable || undefined}
            className="max-h-60 overflow-y-auto"
          >
            {filteredOptions.map((option, index) => {
              const isSelected = selectedValues?.includes(option.value)
              const isActive = index === activeIndex
              return (
                <button
                  type="button"
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  tabIndex={-1}
                  aria-selected={isSelected ?? isActive}
                  aria-disabled={option.disabled || undefined}
                  data-active={isActive || undefined}
                  onClick={() => {
                    if (!option.disabled) {
                      choose(option)
                    }
                  }}
                  onPointerMove={() => {
                    if (!option.disabled && !isActive) {
                      setActiveIndex(index)
                    }
                  }}
                  className={cn(
                    "relative flex w-full cursor-pointer text-left select-none gap-2 rounded-sm px-2 py-1.5",
                    "text-sm text-coval-text",
                    "data-[active]:bg-surface-contrast",
                    option.disabled && "cursor-default opacity-50",
                    option.description ? "items-start" : "items-center",
                  )}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                    <span className="truncate">{option.label}</span>
                    {option.description && (
                      <span className="text-xs leading-snug text-coval-text-muted">
                        {option.description}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <span className="ml-auto flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                      <CheckIcon />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          {showLoadingRow && (
            <p className="flex items-center justify-center gap-2 px-2 py-4 text-sm text-coval-text-muted">
              <Loader size="sm" aria-hidden="true" />
              Loading…
            </p>
          )}
          {showEmptyRow && (
            <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
              {emptyMessage}
            </p>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

Combobox.displayName = "Combobox"

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export { Combobox }
